import Crud from "../Crud";
import { red } from "@mui/material/colors";
import Region from "./Region";
import Location from "./Location";
import UserProfileLocation from "./UserProfileLocation";
import Security from "../Security";

/**
 * Static helper functions related to users.
 */
export default class User extends Crud {
  /**
   * Checks if a user is on a specific team (by manager or membership).
   * @param {object} team The team object.
   * @param {object} user The user object.
   * @param {object} teamUsersIndexed The indexed team users object.
   * @returns {boolean} True if user is on the team.
   */
  static hasTeam(team, user, teamUsersIndexed) {
    if (!team) {
      console.warn("User.hasTeam called without a team.");
      return false;
    }

    const department =
      Security.context?.()?.departmentsIndexed?.[team.departmentId];

    return (
      team.managerId === user.id ||
      department?.executiveId === user.id ||
      department?.directorId === user.id ||
      Object.values(teamUsersIndexed).some((teamUser) => {
        return teamUser.userId === user.id && teamUser.teamId === team.id;
      })
    );
  }

  /**
   * @param {object} user The user object.
   * @returns The display name of the user.
   */
  static getDisplayName(user) {
    if (!user) {
      return null;
    }

    const parts = [];

    // Hangfire should just show up as "System"
    if (user.id === 2396) {
      return "System";
    }

    if (user.firstname !== null) {
      parts.push(user.firstname.trim());
    }

    if (user.lastname !== null) {
      parts.push(user.lastname.trim());
    }

    return parts.join(" ");
  }

  /**
   * @param {object} user The user object.
   * @returns {string|null} The abbreviated display name of the user.
   */
  static getAbbreviatedDisplayName(user) {
    if (!user) {
      return null;
    }

    if (user.id === 2396) {
      return "System";
    }

    const firstName = user.firstname === null ? "" : user.firstname.trim();
    const lastName = user.lastname === null ? "" : user.lastname.trim();

    return [firstName ? `${firstName.charAt(0)}.` : null, lastName || null]
      .filter(Boolean)
      .join(" ");
  }

  /**
   * Determines if a user matches a search query.
   *
   * @param {object} user The user to test.
   * @param {string} query The search query.
   * @returns {boolean} Whether or not the user matches the query.
   */
  static match(user, query) {
    return (
      (user.firstname !== null &&
        user.firstname.toLowerCase().includes(query)) ||
      (user.firstname !== null &&
        user.lastname !== null &&
        ((
          user.firstname.toLowerCase() +
          " " +
          user.lastname.toLowerCase()
        ).includes(query) ||
          (
            user.lastname.toLowerCase() +
            " " +
            user.firstname.toLowerCase()
          ).includes(query) ||
          (
            user.lastname.toLowerCase() +
            ", " +
            user.firstname.toLowerCase()
          ).includes(query) ||
          (
            user.lastname.toLowerCase() +
            "," +
            user.firstname.toLowerCase()
          ).includes(query))) ||
      (user.lastname !== null && user.lastname.toLowerCase().includes(query)) ||
      (user.email !== null && user.email.toLowerCase().includes(query)) ||
      user.id === +query
    );
  }

  /**
   * @param {array} users The list to sort.
   * @returns {array} The sorted list.
   */
  static sort(users) {
    return users.sort((a, b) => {
      return (a.firstname + " " + a.lastname).localeCompare(
        b.firstname + " " + b.lastname,
      );
    });
  }

  /**
   * @returns {object} The color of this object.
   */
  static getBaseColor() {
    return red;
  }

  /**
   * @returns {string} The icon name for this object.
   */
  static getIconName() {
    return "Person";
  }

  /**
   * Get regions the current user has access to.
   *
   * @returns {array}
   */
  static getFilteredRegions(
    user,
    userprofileLocationsIndexed,
    regionsIndexed,
    locationsIndexed,
  ) {
    let regions = [];

    if (Security.hasPermission("user.viewAllRegions") === true) {
      regions = Object.values(regionsIndexed);
    } else {
      const userProfileLocations = UserProfileLocation.read(
        userprofileLocationsIndexed,
        [
          {
            key: "userprofileId",
            value: user.id,
          },
        ],
      );

      const regionIds = {};
      userProfileLocations.forEach((userProfileLocation) => {
        const location = locationsIndexed[userProfileLocation.locationsId];

        // Sales Region
        if (location.regionId && !regionIds[location.regionId]) {
          regionIds[location.regionId] = true;
          regions.push(regionsIndexed[location.regionId]);
        }

        // Field Operations Region
        if (
          location.fieldOperationsRegionId &&
          !regionIds[location.fieldOperationsRegionId]
        ) {
          regionIds[location.fieldOperationsRegionId] = true;
          regions.push(regionsIndexed[location.fieldOperationsRegionId]);
        }
      });
    }

    return Region.sort(regions);
  }

  /**
   * Get locations the current user has access to.
   *
   * @returns {array}
   */
  static getFilteredLocations(
    user,
    userprofileLocationsIndexed,
    locationsIndexed,
  ) {
    let locations = [];

    if (Security.hasPermission("user.viewAllLocations") === true) {
      locations = Object.values(locationsIndexed);
    } else {
      const userProfileLocations = UserProfileLocation.read(
        userprofileLocationsIndexed,
        [
          {
            key: "userprofileId",
            value: user.id,
          },
        ],
      );

      const locationIds = {};
      userProfileLocations.forEach((userProfileLocation) => {
        const location = locationsIndexed[userProfileLocation.locationsId];
        if (!locationIds[location.id]) {
          locationIds[location.id] = true;
          locations.push(location);
        }
      });
    }

    return Location.sort(locations);
  }

  /**
   * Gets billing locations available through the current user's locations.
   *
   * @param {object} user The user whose location access should be used.
   * @param {object} userprofileLocationsIndexed The indexed user locations.
   * @param {object} locationsIndexed The indexed locations.
   * @returns {array} The accessible billing locations.
   */
  static getFilteredBillingLocations(
    user,
    userprofileLocationsIndexed,
    locationsIndexed,
  ) {
    if (Security.hasPermission("user.viewAllLocations") === true) {
      return Location.sort(Object.values(locationsIndexed));
    }

    const billingLocations = [];
    const billingLocationIds = {};
    User.getFilteredLocations(
      user,
      userprofileLocationsIndexed,
      locationsIndexed,
    ).forEach((location) => {
      const billingLocationId = location.billingLocationId
        ? location.billingLocationId
        : location.id;

      if (billingLocationIds[billingLocationId] !== true) {
        billingLocationIds[billingLocationId] = true;
        billingLocations.push(locationsIndexed[billingLocationId]);
      }
    });

    return Location.sort(billingLocations);
  }

  /**
   * Gets regions attached to the billing locations available to a user.
   *
   * @param {object} user The user whose location access should be used.
   * @param {object} userprofileLocationsIndexed The indexed user locations.
   * @param {object} regionsIndexed The indexed regions.
   * @param {object} locationsIndexed The indexed locations.
   * @returns {array} The accessible billing regions.
   */
  static getFilteredBillingRegions(
    user,
    userprofileLocationsIndexed,
    regionsIndexed,
    locationsIndexed,
  ) {
    const billingRegions = [];
    const billingRegionIds = {};
    User.getFilteredBillingLocations(
      user,
      userprofileLocationsIndexed,
      locationsIndexed,
    ).forEach((location) => {
      if (location.regionId && billingRegionIds[location.regionId] !== true) {
        billingRegionIds[location.regionId] = true;
        billingRegions.push(regionsIndexed[location.regionId]);
      }
    });

    return Region.sort(billingRegions);
  }

  /**
   * Get all active users.
   *
   * @returns {array}
   */
  static getActiveUsers(usersIndexed) {
    return this.sort(
      this.read(usersIndexed, [
        {
          key: "active",
          value: true,
        },
      ]).filter((user) => !user.username.includes("api-")),
    );
  }

  /**
   * Get users the current user has access to.
   *
   * @returns {array}
   */
  static getFilteredUsers(
    user,
    userprofileLocationsIndexed,
    locationsIndexed,
    usersIndexed,
    salesPcrLinksIndexed,
  ) {
    let users = [];

    if (Security.hasPermission("user.viewAllUsers") === true) {
      users = this.read(usersIndexed, [
        {
          key: "active",
          value: true,
        },
      ]).filter((user) => !user.username.includes("api-"));
    } else {
      let userIds = [];

      // Add current user
      userIds = userIds.concat([user.id]);

      // Add anyone the current user is a PCR for
      userIds = userIds.concat(
        this.read(salesPcrLinksIndexed, [
          {
            key: "patientCareRepId",
            value: user.id,
          },
        ]).map((salesPcrLink) => salesPcrLink.salesRepId),
      );

      // If the current user is an office manager or area manager, add
      // users from those locations.
      if (Security.hasPermission("user.viewAllUsersForMyLocations")) {
        // Get all of this user's locations
        const locations = this.getFilteredLocations(
          user,
          userprofileLocationsIndexed,
          locationsIndexed,
        );

        // Get all of the users for all of those locations
        userIds = userIds.concat(
          UserProfileLocation.read(userprofileLocationsIndexed, [
            {
              key: "locationsId",
              value: locations.map((location) => location.id),
            },
          ]).map((userprofileLocation) => userprofileLocation.userprofileId),
        );
      }

      // Get the users
      users = this.read(usersIndexed, [
        {
          key: "id",
          value: userIds,
        },
        {
          key: "active",
          value: true,
        },
      ]).filter((user) => !user.username.includes("api-"));
    }

    return this.sort(users);
  }

  /**
   * Get sales rep options for a specific set of locations.
   *
   * @param {object} usersIndexed - The indexed users.
   * @param {object} userprofileLocationsIndexed - The indexed user profile locations.
   * @param {array} locationIds - The location IDs to filter by.
   * @returns {array}
   */
  static getSalesReps(usersIndexed, userprofileLocationsIndexed, locationIds) {
    let users;

    // Determine which roles to include based on current user's role
    const roles = ["SALES", "SALES_MANAGER", "MARKETING_MANAGER"];

    if (locationIds && locationIds.length > 0) {
      // Limit user dropdown to users at the specified location(s).
      users = User.sort(
        User.read(usersIndexed, [
          {
            key: "active",
            value: true,
          },
          {
            key: "role",
            value: roles,
          },
          {
            key: "id",
            value: Object.values(userprofileLocationsIndexed)
              .filter((userprofileLocation) => {
                return locationIds.includes(userprofileLocation.locationsId);
              })
              .map((userProfileLocation) => {
                return userProfileLocation.userprofileId;
              }),
          },
        ]),
      ).filter((user) => !user.username.includes("api-"));
    } else {
      // Only limit the user dropdown to active users.
      users = User.sort(
        User.read(usersIndexed, [
          {
            key: "active",
            value: true,
          },
          {
            key: "role",
            value: roles,
          },
        ]),
      ).filter((user) => !user.username.includes("api-"));
    }
    return users;
  }

  /**
   * Get users by role ID, optionally filtered to one or more locations.
   *
   * @param {object} usersIndexed - The indexed users.
   * @param {object} userprofileLocationsIndexed - The indexed user profile locations.
   * @param {number[]} roleIds - The role IDs to include.
   * @param {array} locationIds - Optional location IDs to filter by.
   * @returns {array}
   */
  static getByRole(
    usersIndexed,
    userprofileLocationsIndexed,
    roleIds,
    locationIds,
  ) {
    const normalizedRoleIdsSet = new Set(
      (roleIds || []).map((roleId) => String(roleId)),
    );
    const userprofileRolesIndexed =
      Security.context?.()?.userprofileRolesIndexed || {};
    const usersInRoleSet = new Set(
      Object.values(userprofileRolesIndexed)
        .filter((userprofileRole) =>
          normalizedRoleIdsSet.has(String(userprofileRole.roleId)),
        )
        .map((userprofileRole) => userprofileRole.userProfileId),
    );

    let usersInLocationSet = null;
    if (locationIds && locationIds.length > 0 && userprofileLocationsIndexed) {
      usersInLocationSet = new Set(
        Object.values(userprofileLocationsIndexed)
          .filter((userprofileLocation) =>
            locationIds.includes(userprofileLocation.locationsId),
          )
          .map((userprofileLocation) => userprofileLocation.userprofileId),
      );
    }

    return User.sort(
      Object.values(usersIndexed).filter((user) => {
        if (user.active !== true || user.username.includes("api-")) {
          return false;
        }

        if (!usersInRoleSet.has(user.id)) {
          return false;
        }

        if (usersInLocationSet && !usersInLocationSet.has(user.id)) {
          return false;
        }

        return true;
      }),
    );
  }

  /**
   * Get technician options for a specific set of locations.
   *
   * @param {object} usersIndexed - The indexed users.
   * @param {object} userprofileLocationsIndexed - The indexed user profile locations (optional).
   * @param {array} locationIds - The location IDs to filter by (optional).
   * @returns {array}
   */
  static getTechnicians(
    usersIndexed,
    userprofileLocationsIndexed,
    locationIds,
  ) {
    let users;
    if (locationIds && locationIds.length > 0 && userprofileLocationsIndexed) {
      // Limit user dropdown to users at the specified location(s).
      users = User.sort(
        User.read(usersIndexed, [
          {
            key: "active",
            value: true,
          },
          {
            key: "role",
            value: [
              "TECHNICIAN",
              "AREA_MANAGER",
              "REGIONAL_OPERATIONS_MANAGER",
              "OFFICE_MANAGER",
            ],
          },
          {
            key: "id",
            value: Object.values(userprofileLocationsIndexed)
              .filter((userprofileLocation) => {
                return locationIds.includes(userprofileLocation.locationsId);
              })
              .map((userProfileLocation) => {
                return userProfileLocation.userprofileId;
              }),
          },
        ]),
      );
    } else {
      // Only limit the user dropdown to active users.
      users = User.sort(
        User.read(usersIndexed, [
          {
            key: "active",
            value: true,
          },
          {
            key: "role",
            value: [
              "TECHNICIAN",
              "AREA_MANAGER",
              "REGIONAL_OPERATIONS_MANAGER",
              "OFFICE_MANAGER",
            ],
          },
        ]),
      );
    }
    return users;
  }

  /**
   * Get ATP options for a specific set of locations.
   *
   * @param {object} usersIndexed - The indexed users.
   * @param {object} userprofileLocationsIndexed - The indexed user profile locations.
   * @param {array} locationIds - The location IDs to filter by.
   * @returns {array}
   */
  static getATPs(usersIndexed, userprofileLocationsIndexed, locationIds) {
    let users;
    if (locationIds && locationIds.length > 0) {
      // Limit user dropdown to users at the specified location(s).
      users = User.sort(
        User.read(usersIndexed, [
          {
            key: "active",
            value: true,
          },
          {
            key: "isAtp",
            value: true,
          },
          {
            key: "id",
            value: Object.values(userprofileLocationsIndexed)
              .filter((userprofileLocation) => {
                return locationIds.includes(userprofileLocation.locationsId);
              })
              .map((userProfileLocation) => {
                return userProfileLocation.userprofileId;
              }),
          },
        ]),
      );
    } else {
      // Only limit the user dropdown to active users.
      users = User.sort(
        User.read(usersIndexed, [
          {
            key: "active",
            value: true,
          },
          {
            key: "isAtp",
            value: true,
          },
        ]),
      );
    }
    return users;
  }

  /**
   * Get customer service users.
   *
   * @param {object} usersIndexed - The indexed users.
   * @param {object} teamsIndexed - The indexed teams.
   * @param {object} teamUsersIndexed - The indexed team users.
   * @returns {array}
   */
  static getCustomerServiceUsers(usersIndexed, teamsIndexed, teamUsersIndexed) {
    let users = [];
    let customerExperienceTeam = Object.values(teamsIndexed).find(
      (team) => team.id === 6,
    );
    let customerExperienceTeamUsers = Object.values(teamUsersIndexed).filter(
      (teamUser) => teamUser.teamId === 6,
    );

    customerExperienceTeamUsers.forEach((teamUser) => {
      let user = usersIndexed[teamUser.userId];
      let manager = usersIndexed[customerExperienceTeam.managerId];

      if (user) {
        users.push({
          name: `${user.firstname} ${user.lastname}`,
          id: user.id,
          active: user.active,
        });
      }

      if (manager && !users.some((u) => u.id === manager.id)) {
        users.push({
          name: `${manager.firstname} ${manager.lastname}`,
          id: manager.id,
          active: manager.active,
        });
      }

      users.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    });

    return users;
  }

  /**
   * Get compliance users.
   *
   * @param {object} usersIndexed - The indexed users.
   * @param {object} teamsIndexed - The indexed teams.
   * @param {object} teamUsersIndexed - The indexed team users.
   * @returns {array}
   */
  static getComplianceUsers(usersIndexed, teamsIndexed, teamUsersIndexed) {
    let users = [];
    let complianceTeam = Object.values(teamsIndexed).find(
      (team) => team.id === 25,
    );
    let complianceTeamUsers = Object.values(teamUsersIndexed).filter(
      (teamUser) => teamUser.teamId === 25,
    );

    complianceTeamUsers.forEach((teamUser) => {
      let user = usersIndexed[teamUser.userId];
      let manager = usersIndexed[complianceTeam.managerId];

      if (user) {
        users.push({
          name: `${user.firstname} ${user.lastname}`,
          id: user.id,
          active: user.active,
        });
      }

      if (manager && !users.some((u) => u.id === manager.id)) {
        users.push({
          name: `${manager.firstname} ${manager.lastname}`,
          id: manager.id,
          active: manager.active,
        });
      }

      users.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    });

    return users;
  }
}
